import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications, LocalNotificationSchema } from '@capacitor/local-notifications';
import { firstValueFrom } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';
import { AuthenticationService } from '../../../../common/services/authentication/authentication.service';
import { LogFactory } from '../../../../common/services/logger/log-factory';
import { SessionResettable } from '../../../../common/services/session-reset/session-reset';
import { WorkoutProgram } from '../models/sequal.models';
import { DbService } from './db.service';
import { PlanScheduleStatus, planProgress } from '../utils/progress-stats.util';

const DAY_MS = 86_400_000;
/** Concrete notifications are topped up this many days ahead; reschedule()
 *  runs on every schedule change and app start, so the window slides. */
const SCHEDULE_AHEAD_DAYS = 14;
/** Per-program notification id space (id = hash(programId)*OFFSET + dayIndex). */
const ID_SPACE = 100;
/** Hash buckets {@link PlanReminderService.hashId} maps a program id into. With
 *  ID_SPACE this bounds the id range this service owns. */
const HASH_BUCKETS = 20_000;
/** Inclusive id range reserved for plan reminders — only ids in it are cancelled
 *  on a reschedule. Any other local notification this app ever schedules MUST
 *  allocate its ids OUTSIDE this range, or a reschedule will silently cancel it. */
const PLAN_ID_MIN = ID_SPACE; // hashId is 1-based, so the lowest base is 1*ID_SPACE
const PLAN_ID_MAX = HASH_BUCKETS * ID_SPACE + ID_SPACE - 1;
/** The range above, for the features that must keep their own ids out of it. */
export const PLAN_REMINDER_ID_RANGE: Readonly<{ min: number; max: number }> = {
  min: PLAN_ID_MIN,
  max: PLAN_ID_MAX,
};
/** iOS keeps only the 64 soonest-firing pending local notifications per app and
 *  silently drops the rest, so the budget is global — across every program — not
 *  per program. Held under 64 for headroom; the window slides on every
 *  reschedule, so truncating the tail only shortens the look-ahead. */
export const MAX_PENDING_NOTIFICATIONS = 60;

/** One candidate notification plus the concrete instant it fires (the budget is
 *  awarded soonest-first, so the fire time has to survive the sort). */
type ScheduledReminder = LocalNotificationSchema & { schedule: { at: Date; allowWhileIdle: boolean } };

/** Reminder copy — shared enum per the string rule (single-use here today, but
 *  surfaced verbatim in tests and docs). */
export enum PlanReminderText {
  TITLE = 'Workout time',
  BODY = 'is ready for you today. A few minutes counts!',
}

/**
 * On-device session reminders for scheduled plans (ADR-0055): a local
 * notification at the program's reminderTime on each day a session is due.
 * No server infra — schedules are computed locally and topped up on every
 * schedule change / app start. Signed-in-only by UI gating (the start-plan
 * flow); the service itself is tier-agnostic.
 */
@Injectable({ providedIn: 'root' })
export class PlanReminderService implements SessionResettable {
  private readonly db = inject(DbService);
  private readonly logger = inject(LogFactory).createLogger(PlanReminderService);

  constructor() {
    // The reminders belong to whoever is signed in: a sign-in schedules theirs
    // (the startup call in AppComponent covers a restored session too).
    // skip(1): the replayed current value is not a sign-in.
    inject(AuthenticationService)
      .userId$()
      .pipe(skip(1), filter((id): id is string => id != null))
      .subscribe(() => void this.reschedule());
  }

  /**
   * Sign-out cancels the previous account's reminders (ADR-0284). They carry
   * its program names, and on a shared phone "<program> is ready for you today"
   * would keep arriving for someone else until the next cold start.
   */
  resetOnLogout(): void {
    void this.cancelOwn();
  }

  /** Ask for (or verify) notification permission — called only when the user
   *  first sets a reminder (progressive, never up-front). */
  async ensurePermission(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    const status = await LocalNotifications.checkPermissions();
    if (status.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req.display === 'granted';
  }

  /**
   * Recompute every plan reminder from current schedule state: cancels the
   * pending notifications in this service's id range (never another feature's),
   * then schedules a concrete notification for each due day in the next
   * {@link SCHEDULE_AHEAD_DAYS} days per scheduled, incomplete program with a
   * reminderTime — capped globally at {@link MAX_PENDING_NOTIFICATIONS}.
   */
  async reschedule(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await this.cancelOwn();
      const programs = (await this.db.getAllPrograms()).filter(
        (p) => !p.deletedAt && p.startDate != null && p.reminderTime,
      );
      if (!programs.length) return;
      const logs = await firstValueFrom(this.db.activityLogs$().pipe(take(1)));
      const plans = new Map(planProgress(programs, logs).map((p) => [p.programId, p]));

      const toSchedule: ScheduledReminder[] = [];
      const now = Date.now();
      for (const program of programs) {
        const plan = plans.get(program.id);
        if (!plan || plan.status === PlanScheduleStatus.COMPLETED) continue;
        const [hour, minute] = this.parseTime(program.reminderTime!);
        if (hour == null) continue;
        const base = this.hashId(program.id) * ID_SPACE;
        for (let i = 0; i < SCHEDULE_AHEAD_DAYS; i++) {
          const day = new Date(now + i * DAY_MS);
          day.setHours(hour, minute ?? 0, 0, 0);
          if (day.getTime() <= now) continue;
          if (!this.isDueOn(program, day.getTime())) continue;
          toSchedule.push({
            id: base + i,
            title: PlanReminderText.TITLE,
            body: `${program.name} ${PlanReminderText.BODY}`,
            schedule: { at: day, allowWhileIdle: true },
          });
        }
      }
      if (!toSchedule.length) return;
      // Soonest-first, then truncate: the platform budget is per app, so the
      // nearest reminders across ALL programs win the slots.
      toSchedule.sort((a, b) => a.schedule.at.getTime() - b.schedule.at.getTime());
      if (toSchedule.length > MAX_PENDING_NOTIFICATIONS) {
        this.logger.debug(
          'Plan reminder budget reached — look-ahead truncated to the soonest reminders',
          { scheduled: MAX_PENDING_NOTIFICATIONS, dropped: toSchedule.length - MAX_PENDING_NOTIFICATIONS },
        );
      }
      await LocalNotifications.schedule({
        notifications: toSchedule.slice(0, MAX_PENDING_NOTIFICATIONS),
      });
    } catch {
      /* reminders are best-effort — never break the flow that triggered this */
    }
  }

  /** Cancels the pending notifications in this service's id range — never another feature's. */
  private async cancelOwn(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const pending = await LocalNotifications.getPending();
      const ours = pending.notifications.filter(
        (n) => n.id >= PLAN_ID_MIN && n.id <= PLAN_ID_MAX,
      );
      if (ours.length) {
        await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
      }
    } catch (err) {
      // Best-effort, like scheduling; a leftover reminder is not worth failing a sign-out.
      this.logger.warn('Could not cancel plan reminders', err as Error);
    }
  }

  /** True when `program` has a session due on the day containing `ms`. */
  private isDueOn(program: WorkoutProgram, ms: number): boolean {
    if (program.startDate == null) return false;
    const start = new Date(program.startDate); start.setHours(0, 0, 0, 0);
    const day = new Date(ms); day.setHours(0, 0, 0, 0);
    const dayNumber = Math.floor((day.getTime() - start.getTime()) / DAY_MS) + 1;
    return dayNumber >= 1 && dayNumber <= program.totalDays;
  }

  private parseTime(hhmm: string): [number | null, number | null] {
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
    return m ? [Number(m[1]), Number(m[2])] : [null, null];
  }

  /** Stable small positive int from a uuid (notification id space). */
  private hashId(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return (h % 20000) + 1; // keep id*100+i well inside 32-bit
  }
}
